import { Translator } from 'Translator';
import classnames from 'classnames';
import LoginForm from 'components/LoginSignUp/LoginForm';
import SignUpFormContainer from 'components/LoginSignUp/SignUpFormContainer';
import RoundedButton from 'components/RoundedButton';
import ExternalLink from 'components/organisms/ExternalLink';
import lscache from 'lscache';
import AuthButtons from 'modules/authentication/components/AuthButtons';
import Separator from 'modules/authentication/components/Separator';
import SubmitButton from 'modules/authentication/components/common/SubmitButton';
import CountryProvider from 'providers/CountryProvider';
import React, { useMemo } from 'react';
import CurrencySelect from 'reusable-components/controls/CurrencySelect';
import { openExternalLinkSimple } from 'utils/openExternalLink';

import CSS from './LoginSignUp.module.scss';

interface LoginSignUpProps {
  errors?: { validation?: string };
  className?: string;
  options?: unknown;
  onSignup: (data: unknown) => void;
  onLogin: (data: unknown) => void;
  onShowSignupEmail: () => void;
  onShowLogin: () => void;
  showLogin: boolean;
  loginButtonLoading: boolean;
  signupButtonLoading: boolean;
  showSignupForm: boolean;
  companyName?: string;
  onLoginAsGuest?: () => void;
}

function LoginPart({ loginButtonLoading, onLogin }) {
  return (
    <LoginForm loginButtonLoading={loginButtonLoading} handleSubmit={onLogin} />
  );
}

function SignUpPart({
  signupButtonLoading,
  options,
  onSignup,
  showSignupForm,
  onShowSignupEmail,
}) {
  if (showSignupForm) {
    return (
      <CountryProvider>
        {({ countries }) => {
          return (
            <SignUpFormContainer
              options={options}
              signupButtonLoading={signupButtonLoading}
              handleSubmit={onSignup}
              countries={countries}
            />
          );
        }}
      </CountryProvider>
    );
  }

  return (
    <SubmitButton
      text={Translator.translate(
        'Forms.Buttons.signUpWithEmail',
        {},
        true,
        'Sign up with email',
      )}
      onSubmit={onShowSignupEmail}
    />
  );
}

const onLinkClick = (e) => {
  openExternalLinkSimple(e.currentTarget.getAttribute('data-link'));
};

function getLink(target: string, text: string) {
  return (
    <button
      className={CSS.Link}
      onClick={onLinkClick}
      data-link={`${URL_HOST}${target}`}
    >
      {text}
    </button>
  );
}

function LoginSignUp({
  errors,
  className,
  options,
  onSignup,
  onLogin,
  onShowSignupEmail,
  onShowLogin,
  showLogin,
  loginButtonLoading,
  signupButtonLoading,
  showSignupForm,
  companyName,
  onLoginAsGuest,
}: LoginSignUpProps) {
  const termsAndConditionsLink = getLink(
    '/info/terms-and-conditions/',
    Translator.translate('Forms.TermsAndPolicy.termsAndConditions', {}, true),
  );
  const privacyPolicyLink = getLink(
    '/info/privacy-policy/',
    Translator.translate('Forms.TermsAndPolicy.privacyPolicy', {}, true),
  );

  const lastUsedProvider = useMemo(() => lscache.get('lastUsedProvider'), []);

  return (
    <div className={classnames(CSS.LoginSignUp, className)}>
      <AuthButtons
        companyName={companyName}
        lastUsedProvider={lastUsedProvider}
        variant="compact"
      />
      <Separator />
      {showLogin ? (
        <LoginPart loginButtonLoading={loginButtonLoading} onLogin={onLogin} />
      ) : (
        <SignUpPart
          options={options}
          onSignup={onSignup}
          showSignupForm={showSignupForm}
          onShowSignupEmail={onShowSignupEmail}
          signupButtonLoading={signupButtonLoading}
        />
      )}
      {errors && <div className={CSS.errors}>{errors.validation}</div>}
      {onLoginAsGuest && (
        <RoundedButton
          onClick={onLoginAsGuest}
          invertedBorder
          className={CSS.GuestLogin}
        >
          Continue as guest
        </RoundedButton>
      )}
      {showLogin && (
        <div className={CSS.Links}>
          <button className={CSS.LinkButton} onClick={onShowSignupEmail}>
            {Translator.translate(
              'Forms.Buttons.noAccountSignUpHere',
              {},
              true,
              'No account yet? Sign up here',
            )}
          </button>
          <ExternalLink
            className={`${CSS.LinkButton} ${CSS.ForgotPassword}`}
            url={`${URL_STOREFRONT_HOST}/password/reset`}
          >
            {Translator.translate(
              'Forms.ForgetPassword.forgetPassword',
              {},
              true,
              'I forgot my password',
            )}
          </ExternalLink>
        </div>
      )}
      {!showLogin && (
        <div className={CSS.Links}>
          <span>
            {Translator.translate(
              'Forms.Buttons.alreadyHaveAnAccountLogin',
              {},
              true,
              'Already have an account? Log in here',
            )}{' '}
            <button
              className={CSS.LinkButton}
              onClick={onShowLogin}
              data-test-id={'loginHere'}
            >
              {Translator.translate(
                'Forms.LoginSignup.logInHere',
                {},
                true,
                'Log in here',
              )}
            </button>
          </span>
          {showSignupForm && (
            <p className={CSS.Policy}>
              {Translator.translate(
                'Forms.TermsAndPolicy.acceptTermsAndPolicy',
                {
                  termsAndConditionsLink,
                  privacyPolicyLink,
                },
              )}
            </p>
          )}
        </div>
      )}
      <CurrencySelect
        className={CSS.CurrencySelect}
        trackingId="Checkout signup"
      />
    </div>
  );
}

export default LoginSignUp;
